import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { User } from '../services/auth.service';
import { redirectAccessDenied } from './guard-navigation';

/**
 * Privilege guards must not fall back to a cached `currentUser` when `/user` fails.
 * Only a fresh API payload (non-cached) is authoritative.
 */
export function resolveFreshAuthUser(response: unknown): User | null {
  const payload = response as { data?: User; cached?: boolean } | User | null | undefined;
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  if ('cached' in payload && payload.cached) {
    return null;
  }
  if ('data' in payload && payload.data) {
    return payload.data;
  }
  // Bare user object (tests / alternate shapes)
  if ('id' in payload && !('success' in payload)) {
    return payload as User;
  }
  return null;
}

export function getPartnerStatus(user: User | null | undefined): string {
  return String(user?.partner_status || 'NONE').toUpperCase();
}

export function isAdminUser(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.account_type === 'admin') return true;

  const roles = (user as User & { roles?: string[] }).roles;
  return Array.isArray(roles) && roles.some((role) => String(role).toLowerCase() === 'admin');
}

/**
 * Manager dashboard access — only APPROVED partners (account_type often stays "user").
 */
export function isPartnerApproved(user: User | null | undefined): boolean {
  if (!user) return false;
  return getPartnerStatus(user) === 'APPROVED';
}

/** @deprecated Prefer isPartnerApproved — manager access requires APPROVED status. */
export function isPartnerUser(user: User | null | undefined): boolean {
  return isPartnerApproved(user);
}

export function hasPendingPartnerApplication(user: User | null | undefined): boolean {
  const status = getPartnerStatus(user);
  return status === 'PENDING' || status === 'PENDING_APPROVAL';
}

export function isPartnerApplicant(user: User | null | undefined): boolean {
  if (!user) return false;
  const status = getPartnerStatus(user);
  return status === 'PENDING' || status === 'PENDING_APPROVAL' || status === 'REJECTED';
}

export function isPartnerPendingApprovalRoute(url: string): boolean {
  const path = String(url || '').split('?')[0];
  return path === '/manager/pending-approval' || path.startsWith('/manager/pending-approval/');
}

export function redirectPartnerPendingApproval(router: Router): UrlTree {
  return router.createUrlTree(['/manager/pending-approval']);
}

/** Menu entry "Devenir partenaire": visible until the partner is approved. */
export function canOpenBecomePartnerEntry(user: User | null | undefined): boolean {
  if (!user || isAdminUser(user)) return false;
  return !isPartnerApproved(user);
}

/** True when the user can still submit a first become-partner application. */
export function canApplyToBecomePartner(user: User | null | undefined): boolean {
  if (!user || isAdminUser(user)) return false;
  return getPartnerStatus(user) === 'NONE';
}

export function createLoginUrlTree(router: Router, state: RouterStateSnapshot): UrlTree {
  return router.createUrlTree(['/auth/login'], {
    queryParams: { returnUrl: state.url },
  });
}

export function redirectToLogin(router: Router, state: RouterStateSnapshot): UrlTree {
  return createLoginUrlTree(router, state);
}

export function isUserPendingDeletion(user: User | null | undefined): boolean {
  if (!user) return false;
  return Boolean(user.is_pending_deletion || user.status === 'PENDING_DELETION');
}

export function createPendingDeletionUrlTree(router: Router): UrlTree {
  return router.createUrlTree(['/auth/pending-deletion']);
}

export function evaluateAdminAccess(
  user: User | null,
  router: Router,
  state: RouterStateSnapshot,
): boolean | UrlTree {
  if (!user) {
    return redirectToLogin(router, state);
  }

  if (isUserPendingDeletion(user)) {
    return createPendingDeletionUrlTree(router);
  }

  if (isAdminUser(user)) {
    return true;
  }

  return redirectAccessDenied(router, state, 'admin');
}

export function redirectPartnerDashboard(router: Router): UrlTree {
  return router.createUrlTree(['/manager/dashboard']);
}

export function evaluatePartnerAccess(
  user: User | null,
  router: Router,
  state: RouterStateSnapshot,
): boolean | UrlTree {
  if (!user) {
    return redirectToLogin(router, state);
  }

  if (isUserPendingDeletion(user)) {
    return createPendingDeletionUrlTree(router);
  }

  const onPendingPage = isPartnerPendingApprovalRoute(state.url);

  if (isPartnerApproved(user)) {
    if (onPendingPage) {
      return redirectPartnerDashboard(router);
    }
    return true;
  }

  // Pending / rejected applicants may only see the waiting page.
  if (isPartnerApplicant(user)) {
    return onPendingPage ? true : redirectPartnerPendingApproval(router);
  }

  return redirectAccessDenied(router, state, 'partner');
}
